import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth.jsx";

/**
 * ShareBanner — always visible while a share-link session is active.
 *
 * Opening a share link deliberately REPLACES any signed-in session in that
 * browser, because holding two identities at once is how confused-deputy bugs
 * start. That is the right security call, but on its own it produces a nasty
 * surprise: an owner who opens their own link in their normal browser is
 * silently demoted to an anonymous viewer, and every subsequent 401 and 403
 * looks like a broken app rather than a changed identity.
 *
 * So the swap is never silent. This says plainly what you are, and gives one
 * click back out.
 */
export default function ShareBanner() {
  const { share, refresh } = useAuth();
  const navigate = useNavigate();

  // Two different states wear the same bar:
  //   share    — a real link visitor, no account, session-backed
  //   preview  — the owner looking at what a visitor sees, still signed in
  // Only the first involves any identity change, which is why only the first
  // has to call the server to leave.
  let preview = null;
  try {
    const id = sessionStorage.getItem("cg_preview_client");
    if (id) preview = { id, name: sessionStorage.getItem("cg_preview_name") || null };
  } catch { /* storage unavailable */ }

  if (!share && !preview) return null;

  async function exitShare() {
    await fetch("/api/share/close", { method: "POST" }).catch(() => {});
    await refresh();
    navigate("/", { replace: true });
  }

  function exitPreview() {
    try {
      sessionStorage.removeItem("cg_preview_client");
      sessionStorage.removeItem("cg_preview_name");
    } catch { /* storage unavailable */ }
    navigate("/family", { replace: true });
  }

  if (share) {
    const name = share.display_name;
    return (
      <div style={s.bar}>
        <span style={s.text}>
          <span style={s.pill}>Read-only</span>
          {name
            ? `You're viewing ${name}'s profile through a shared link.`
            : "You're viewing a shared profile."}
        </span>
        <button style={s.btn} onClick={exitShare}>Exit shared view</button>
      </div>
    );
  }

  return (
    <div style={s.bar}>
      <span style={s.text}>
        <span style={s.pill}>Preview</span>
        {preview.name
          ? `This is roughly what someone with a link to ${preview.name}'s profile sees. You're still signed in.`
          : "This is roughly what someone with a link sees. You're still signed in."}
      </span>
      <button style={s.btn} onClick={exitPreview}>Exit preview</button>
    </div>
  );
}

const s = {
  bar:  { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          padding: "8px 20px", background: "var(--color-accent-subtle)",
          borderBottom: "1px solid var(--color-border)", flexWrap: "wrap" },
  text: { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)",
          display: "flex", alignItems: "center", gap: 10 },
  pill: { fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em",
          fontWeight: "var(--weight-semibold)", color: "var(--color-accent)",
          border: "1px solid var(--color-accent)", borderRadius: 999, padding: "2px 8px" },
  btn:  { fontSize: "var(--text-xs)", background: "none",
          border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)",
          padding: "5px 10px", color: "var(--color-text-secondary)", cursor: "pointer",
          font: "inherit", whiteSpace: "nowrap" },
};
